<template>
  <div id="app" class="h-100">
    <router-view />
    <!-- Consent banner (pixels spec §5a). Gated behind issuer_pixels_v1 so
         merging Phase 2 to develop changes nothing user-visible until Levi
         flips the flag at launch (safe dark-launch posture; the §10.1 step-2
         ordering of "banner before flag" is satisfied by flipping this flag
         first at launch time). -->
    <consent-banner v-if="consentBannerEnabled" :home-id="routeHomeId" />
  </div>
</template>

<script>
import ConsentBanner from "@/components/privacy/ConsentBanner.vue";
import { socialMeta, appUrl } from "@/utils/seo";

const DEFAULT_TITLE = "Invown - Marketplace";
const DEFAULT_DESCRIPTION =
  "Invown is a two-sided marketplace where companies offer investment opportunities to everyday investors.";
const DEFAULT_OG_IMAGE = "https://invown.com/app/favicon/android-chrome-192x192.png";

export default {
  components: { ConsentBanner },
  computed: {
    consentBannerEnabled() {
      try {
        return !!(this.$unleash && this.$unleash.isEnabled("issuer_pixels_v1"));
      } catch (e) {
        return false;
      }
    },
    routeHomeId() {
      const id = this.$route && this.$route.params && this.$route.params.home_id;
      return Number(id) || 0;
    },
  },
  metaInfo() {
    // The /app SPA is public-by-default for SEO in production: we want as much
    // indexed as possible (offerings, pitch pages, browse-all, etc.). The only
    // pages held back are login-walled ones (meta.requiresLogin) — a crawler
    // can't reach them, so indexing would just produce soft-404s; they get
    // noindex,follow. meta.noindex opts any public page out, and
    // meta.indexable force-indexes a login-walled one. Every non-production
    // build is forced to noindex,nofollow so a leaked dev/staging host can
    // never be crawled. See docs/seo/dev-deindex-and-gsc-runbook.md.
    const prodIndexable = process.env.VUE_APP_SEO_INDEXABLE === "true";
    // Inspect every matched record, not just the leaf: requiresLogin/noindex
    // are sometimes declared on a parent route (e.g. /edit-profile, /admin),
    // and vue-router 3 does not reliably merge parent meta into $route.meta.
    // This mirrors the global auth guard's to.matched.some(...) pattern.
    const matched = (this.$route && this.$route.matched) || [];
    const metaFlag = (key) =>
      matched.some((r) => r && r.meta && r.meta[key]) ||
      !!(this.$route && this.$route.meta && this.$route.meta[key]);
    const isPrivate =
      (metaFlag("requiresLogin") || metaFlag("noindex")) && !metaFlag("indexable");
    const indexable = prodIndexable && !isPrivate;
    const robots = !prodIndexable
      ? "noindex,nofollow"
      : isPrivate
      ? "noindex,follow"
      : "index,follow";

    const url = appUrl(this.$route && this.$route.fullPath);

    const info = {
      title: DEFAULT_TITLE,
      meta: [
        { vmid: "description", name: "description", content: DEFAULT_DESCRIPTION },
        { property: "og:site_name", content: "Invown" },
        { name: "twitter:site", content: "@invown" },
        // Default social tags. Each carries a vmid so a page-level metaInfo()
        // overrides it instead of emitting a duplicate tag.
        ...socialMeta({
          title: DEFAULT_TITLE,
          description: DEFAULT_DESCRIPTION,
          image: DEFAULT_OG_IMAGE,
          url,
        }),
        // vmid keeps a single managed robots tag that a route component can
        // override by emitting its own { vmid: "robots", ... }.
        { vmid: "robots", name: "robots", content: robots },
      ],
    };

    // Only emit a canonical for pages we actually want indexed. A canonical on
    // a noindex page sends mixed signals; an absolute prod origin keeps it
    // pointed at invown.com even if the page is reached on another host.
    if (indexable) {
      info.link = [{ vmid: "canonical", rel: "canonical", href: url }];
    }

    return info;
  },
};
</script>

<style>
html,
body {
  height: 100%;
  @font-face {
    font-family: Inter;
    font-display: swap;
  }
}

.modal-fullscreen .modal-dialog {
  max-width: min(1000px, calc(100% - 3.5rem));
  height: 100%;
}

/* Ensure tooltips render above modals (modal-body is z-index:99999 in custom.scss) */
.tooltip {
  z-index: 10000000 !important;
  pointer-events: none;
}

/* Center text+icon in ALL buttons globally */
.btn,
button.btn,
a.btn,
.btn-primary,
.btn-secondary,
.btn-success,
.btn-danger,
.btn-warning,
.btn-info,
.btn-outline-primary,
.btn-outline-secondary,
.btn-toggle,
.btn-touch,
.btn-touch-sm,
[class*="btn-"] {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
  gap: 4px;
}
/* Ensure btn-block stays full-width when inline-flex */
.btn.btn-block,
.btn.w-100 {
  display: flex !important;
  width: 100% !important;
}
.tooltip .tooltip-inner {
  max-width: 300px;
  text-align: left;
  font-size: 13px;
  padding: 6px 10px;
}

.my-carousel .carousel-indicators li {
  background-color: red;
}
.my-carousel,
.my-carousel .carousel-inner,
.my-carousel img {
  height: 320px;
  width: 450px;
}
.my-carousel img {
  object-fit: cover;
  border-radius: 1.6rem;
  border-bottom-right-radius: 0;
}
.my-carousel .carousel-caption {
  color: red;
  text-align: left;
  bottom: 0;
  left: 8%;
  padding-bottom: 5px;
}

/* ── Unified Tooltip Icon Style (TT-4) ── */
.invown-tip-icon {
  color: #6c757d;
  font-size: 0.875rem;
  vertical-align: middle;
  transition: color 0.15s;
  cursor: help;
}
.invown-tip-icon:hover {
  color: #495057;
}
.invown-tooltip__trigger {
  cursor: help;
  outline: none;
  display: inline-flex;
  align-items: center;
}

/* ── Global Button Text Centering (all .btn variants) ── */
.btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 4px;
}
</style>

<!-- 
max-width: 100%;
margin: 0;
top: 0;
bottom: 0;
left: 0;
right: 0;
display: flex;
position: fixed;
z-index: 100000;
-->
